<template>
    <div id="awaitingTickets">
        <a-table
            :columns="columns()"
            :dataSource="waitingChatGetter"
            :locale="{
                emptyText: $t('message.noVisitorFound'),
            }"
            :pagination="false"
            class="custom-table2"
            rowKey="_id"
            size="small"
            @change="toggleSortOrder"
        >
            <div slot="email" slot-scope="_, item">
                {{ getVisitorValue(item, "email") }}
            </div>
            <div slot="phone" slot-scope="_, item">
                {{ getVisitorValue(item, "phone") }}
            </div>
            <div slot="name" slot-scope="_, item">
                {{
                    !!item.conversation
                        ? item.conversation.fullname || "-"
                        : !!item.osrPayload
                        ? item.osrPayload.fullname
                        : "-"
                }}
            </div>
            <div slot="createdDate" slot-scope="createdDate">
                {{ createdDate | date }}
            </div>
            <div slot="assignedUser" slot-scope="assignedUser, item">
                <a-select
                    :default-value="assignedUser ? assignedUser._id : null"
                    class="ticket-select"
                    @change="(value) => handleChange(value, item._id)"
                >
                    <a-select-option v-for="user in users.data" :key="user._id" :value="user._id">
                        {{ user.name }}
                    </a-select-option>
                </a-select>
            </div>
            <div slot="status" slot-scope="status, item">
                <a-select
                    :default-value="!!status ? status : null"
                    class="ticket-select"
                    @change="(value) => handleChangeStatus(item._id, value)"
                >
                    <a-select-option v-for="status in statuses" :key="status._id" :value="status.title">
                        {{ status.title }}
                    </a-select-option>
                </a-select>
            </div>

            <div slot="_id" slot-scope="_id, item">
                <div class="d-flex w-100 justify-content-start">
                    <a-button
                        v-if="checkPermission(PERMISSION_TYPES['offlineSupportRequest.update'].tr.name)"
                        class="actionButton d-flex align-items-center"
                        @click="view(item)"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="$t('message.sendResponse')"
                    >
                        <ActionEdit />
                    </a-button>
                    <a-button
                        v-if="checkPermission(PERMISSION_TYPES['offlineSupportRequest.delete'].tr.name)"
                        class="actionButton d-flex align-items-center"
                        @click="archived(item)"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="$t('message.archived')"
                    >
                        <md-icon style="color: #3fc5bd">inventory_2</md-icon>
                    </a-button>
                </div>
            </div>
        </a-table>
        <div class="d-flex justify-content-end mt-3" style="margin-bottom: 100px">
            <Pagination
                :page-change="pageChange"
                :page-sizes="pageSizes"
                :total="waitingTickets.totalPages * perPage"
                pageSizeLocalStorageKey="ticket-page-size"
            />
        </div>
        <AwaitingTicketDetails
            v-if="showingTicket"
            :ticket="showingTicket"
            @close="showingTicket = null"
            @confirm="sendResponse"
            @sendAsComment="sendAsComment"
        />
    </div>
</template>

<script>
import Pagination from "../../../../components/Pagination.vue";
import ActionEdit from "../../../../components/svg/ActionEdit.vue";
import { tableSorter } from "../../../../utils/utils";
import { mapActions, mapGetters, mapState } from "vuex";
import { log2 } from "../../../../utils/log2";
import { getPersistenceData } from "../../../../utils/persistenceStorage";
import { addTicketStatus } from "../../../../api/ticket";
import systemPubSub from "../../../../utils/systemPubSub";
import { filter } from "rxjs/operators";
import { PERMISSION_TYPES } from "../../../../language/permissionTypes";

import AwaitingTicketDetails from "./AwaitingTicketDetails.vue";
import { sendSocketMessage } from "../../../../utils/socketStream";

const log = log2("AwaitingTickets");
export default {
    name: "AwaitingTickets",
    components: {
        Pagination,
        ActionEdit,
        AwaitingTicketDetails,
    },
    props: {
        tag: { type: String },
        platform: { type: String },
        statuses: { type: Array },
        showMyTickets: { type: Boolean },
    },
    data() {
        return {
            page: 1,
            perPage: !isNaN(getPersistenceData("ticket-page-size") || "undefined")
                ? parseInt(getPersistenceData("ticket-page-size"))
                : 10,
            currentSort: {
                columnKey: "createdDate",
                order: "ascend",
            },
            pageSizes: ["10", "25", "50", "100"],
            columns: () => [
                {
                    title: this.$t("headBar.email"),
                    slot: "email",
                    scopedSlots: { customRender: "email" },
                    ...tableSorter("conversation.email", "number", this.currentSort),
                },
                {
                    title: this.$t("headBar.phone"),
                    slot: "phone",
                    scopedSlots: { customRender: "phone" },
                    ...tableSorter("conversation.phone", "number", this.currentSort),
                },
                {
                    title: this.$t("headBar.name"),
                    slot: "name",
                    scopedSlots: { customRender: "name" },
                    ...tableSorter("conversation.fullName", "string", this.currentSort),
                },
                {
                    dataIndex: "createdDate",
                    title: this.$t("headBar.date"),
                    scopedSlots: { customRender: "createdDate" },
                    slot: "createdDate",
                    ...tableSorter("createdDate", "date", this.currentSort),
                },
                {
                    title: this.$t("headBar.subject"),
                    dataIndex: "tag",
                    key: "tag",
                    ...tableSorter("tag", "string", this.currentSort),
                },
                {
                    dataIndex: "status",
                    title: this.$t("headBar.situation"),
                    scopedSlots: { customRender: "status" },
                    key: "status",
                    ...tableSorter("status", "string", this.currentSort),
                },
                {
                    dataIndex: "assignedUser",
                    title: this.$t("headBar.representative"),
                    scopedSlots: { customRender: "assignedUser" },
                    slot: "assignedUser",
                    width: "10%",
                    ...tableSorter("assignedUser.name", "string", this.currentSort),
                },
                {
                    dataIndex: "_id",
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "_id" },
                    slot: "_id",
                    width: "15%",
                    ...tableSorter("_id", "string", this.currentSort),
                },
            ],
            assignedUser: null,
            unSubs: [],
            showingTicket: null,
        };
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            waitingTickets: (state) => state.ticket.waitingTickets,
            topics: (state) => state.ticket.topics,
            users: (state) => state.user.users,
            user: (state) => state.auth.user,
        }),
        ...mapGetters({
            waitingChatGetter: "ticket/getWaitingTickets",
            getUser: "user/getUser",
            checkPermission: "auth/checkPermission",
        }),
    },
    methods: {
        ...mapActions({
            fetchWaitingTickets: "ticket/fetchWaitingTickets",
            assignRepresentative: "ticket/assignRepresentative",
            getUsers: "user/getUsers",
            loadOldMessages: "chat/loadOldMessages",
            sendTicketResponse: "ticket/sendTicketResponse",
        }),
        getVisitorValue(item, field) {
            if (field === "email") log("getVisitorValue", item, field);
            let value = !!item.conversation
                ? item.conversation[field] || "-"
                : !!item.osrPayload
                ? item.osrPayload[field]
                : "-";
            if (item.createdByChatbot) {
                if (field === "email") {
                    value = item.visitorEmailAddress || value;
                }
                if (field === "phone") {
                    value = item.visitorPhoneNumber || value;
                }
            }
            return value;
        },
        handleChange(repId, _id) {
            this.assignRepresentative({ _id, repId });
        },
        handleChangeStatus(id, title) {
            addTicketStatus({ id, title });
        },
        pageChange(page, perPage) {
            this.page = page;
            this.perPage = perPage;
        },
        view(item) {
            this.showingTicket = item;
        },
        sendResponse({ selectedHistory, responseText }) {
            let data = {
                _id: selectedHistory.ticket._id,
                repId: this.user._id,
                response: responseText,
            };
            this.sendTicketResponse(data).then((x) => {
                this.showingTicket = null;
            });
        },
        sendAsComment({ selectedHistory, responseText, isPrivate }) {
            let data = {
                _id: selectedHistory.ticket._id,
                repId: this.user._id,
                response: responseText,
            };
            this.sendTicketResponse(data).then((x) => {
                sendSocketMessage("meta reply-comment", {
                    siteId: selectedHistory.ticket.siteId,
                    channelId: selectedHistory.ticket.visitor.channel,
                    commentId: selectedHistory.ticket.metaComment,
                    message: responseText,
                    isPrivate,
                    type: selectedHistory.ticket.type,
                });
                this.showingTicket = null;
            });
        },
        archived(item) {
            let data = {
                _id: item._id,
                repId: this.user._id,
                response: `Archived by ${this.user.name}`,
            };
            this.sendTicketResponse(data);
        },
        toggleSortOrder(columnKey) {
            if (this.currentSort.columnKey === columnKey) {
                this.currentSort.order = this.currentSort.order === "ascend" ? "descend" : "ascend";
            } else {
                this.currentSort.columnKey = columnKey;
                this.currentSort.order = "ascend";
            }
        },
    },
    mounted() {
        this.toggleSortOrder("createdDate");
        this.fetchWaitingTickets({
            page: this.page,
            perPage: this.perPage,
            tag: this.tag,
            assignedUser: this.assignedUser,
            platform: this.platform,
        });
        const newTicketSub = systemPubSub.stream.pipe(filter((x) => x.type === "ticket_message")).subscribe((x) => {
            this.fetchWaitingTickets({
                page: this.page,
                perPage: this.perPage,
                tag: this.tag,
                assignedUser: this.assignedUser,
                platform: this.platform,
            });
        });
        this.unSubs.push(() => newTicketSub.unsubscribe());
        this.getUsers({
            page: 1,
            perPage: 1000,
        });
    },
    beforeDestroy() {
        this.unSubs.forEach((sub) => sub());
        clearInterval(this.refreshTimerId);
    },
    watch: {
        page(newValue) {
            this.fetchWaitingTickets({
                page: newValue,
                perPage: this.perPage,
                tag: this.tag,
                assignedUser: this.assignedUser,
                platform: this.platform,
            });
        },
        perPage(newValue) {
            this.fetchWaitingTickets({
                page: this.page,
                perPage: newValue,
                tag: this.tag,
                assignedUser: this.assignedUser,
                platform: this.platform,
            });
        },
        tag(newValue) {
            this.fetchWaitingTickets({
                page: this.page,
                perPage: this.perPage,
                tag: newValue,
                assignedUser: this.assignedUser,
                platform: this.platform,
            });
        },
        platform(newValue) {
            this.fetchWaitingTickets({
                page: this.page,
                perPage: this.perPage,
                tag: this.tag,
                assignedUser: this.assignedUser,
                platform: newValue,
            });
        },
        showMyTickets(newValue) {
            if (newValue) {
                this.assignedUser = this.user._id;
                this.fetchWaitingTickets({
                    page: this.page,
                    perPage: this.perPage,
                    tag: this.tag,
                    assignedUser: this.assignedUser,
                    platform: this.platform,
                });
            } else {
                this.assignedUser = null;
                this.fetchWaitingTickets({
                    page: this.page,
                    perPage: this.perPage,
                    tag: this.tag,
                    assignedUser: this.assignedUser,
                    platform: this.platform,
                });
            }
        },
    },
};
</script>

<style lang="scss">
#awaitingTickets {
    .ticket-select {
        width: 100%;

        .ant-select-selection {
            border-color: transparent !important;
        }
    }

    .custom-table {
        background-color: #ffffff;
        border-radius: 6px;
        padding: 10px 20px;
    }

    .ant-table-column-sorters,
    .ant-table-header-column {
        width: 100%;
    }

    .ant-table-header-column {
        padding: 0 10px;
    }

    .table-custom,
    .ant-table-placeholder,
    .ant-table-small {
        border: none;
    }

    .ant-table-thead tr {
    }

    .ant-table-body {
        tr {
        }

        td {
            padding: 0 8px !important;
            font-size: 12px;
            font-weight: 300;
            color: #181c32;
            border-bottom: none;
        }

        th {
            border-right: 1px solid #ebedf3;
            border-bottom: none;
            padding: 0 !important;

            &:last-child {
                border-right: none;
            }
        }
    }

    .ant-table-body tbody td {
        border-right: 1px solid #ebedf3;

        &:last-child {
            border-right: none;
        }
    }

    .ant-table-column-title {
        color: #181c32;
        font-size: 13px;
        font-weight: 300;
        font-family: Poppins;
        white-space: nowrap;
    }

    .actionButton {
        border: none;
        box-shadow: none;
    }
}

.userInfo {
    font-family: Poppins;

    &.first {
        color: #3699ff;
        font-size: 16px;
        font-weight: 500;
        padding-top: 15px;
        line-height: 1;
    }

    &.sec {
        color: #181c32;
        font-size: 14px;
        font-weight: 300;
    }
}

.waiting-ticket-messages {
    max-height: 550px !important;
    overflow: scroll;
}

#awaiting-message-details {
    .ant-modal {
        width: 730px !important;
    }
}

#ticket-response {
    .actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 5px;
        padding: 5px;
        border-top: 1px solid var(--color-border-default);
    }
}
</style>
